import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FOR_BANK_SECTIONS, FOR_BANK_SUB_SECTIONS } from 'constants/businessToolSections/forBank';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';

import { FieldArrayComponent } from 'components/_basic/Form/FieldArray';
import { TextInputControl } from 'components/_basic/Form/TextInput';
import { FormHeader } from 'components/_basic/FormHeader/FormHeader';
import { FormSectionCard } from 'components/_basic/FormSectionCard/FormSectionCard';
import { FORM_SECTION_CARD_VARIANT } from 'components/_basic/FormSectionCard/types';

import { concatWithDot } from 'helpers/string';

import { ProductAndServicesArrayRow } from './components/ProductAndServicesArrayRow';
import { prefix, productAndServicesArrayInitialValues, PRODUCTS_AND_SERVICES_DESCRIPTION_NAMES } from './utils/form';

export const ProductAndServicesDescription = () => {
  const { t } = useTranslation();
  const { control } = useFormContext();

  const canAccess = useCheckSubSectionAccess(
    FOR_BANK_SECTIONS.PRODUCTS_AND_SERVICES,
    FOR_BANK_SUB_SECTIONS.FOR_BANK_PRODUCTS_AND_SERVICES.DESCRIPTION,
  );

  return (
    <>
      <FormSectionCard variant={FORM_SECTION_CARD_VARIANT.LARGE}>
        <FormHeader
          locked={!canAccess}
          title={t('productsAndServicesForm.title')}
          badgeText={t('productsAndServicesForm.description.title')}
          section={FOR_BANK_SECTIONS.PRODUCTS_AND_SERVICES}
        />
      </FormSectionCard>
      {/* 3.1.1 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('productsAndServicesForm.description.purposeAndApplicationArea')}
        aiFieldName={concatWithDot(prefix, PRODUCTS_AND_SERVICES_DESCRIPTION_NAMES.PRODUCTS_OR_SERVICES_AND_THEIR_PURPOSE)}
      >
        <TextInputControl
          multiline
          control={control}
          id="productsAndServicesForm.productAndServices.area"
          name={concatWithDot(prefix, PRODUCTS_AND_SERVICES_DESCRIPTION_NAMES.PRODUCTS_OR_SERVICES_AND_THEIR_PURPOSE)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
      <FormSectionCard
        disabled={!canAccess}
        title={t('productsAndServicesForm.description.keyFeaturesAndBenefits')}
        collapsible
      >
        {/* 3.1.2 */}
        <FieldArrayComponent
          name={concatWithDot(prefix, PRODUCTS_AND_SERVICES_DESCRIPTION_NAMES.KEY_FEATURES_AND_BENEFITS)}
          initialItem={productAndServicesArrayInitialValues}
          buttonText={t('product/service')}
          disabled={!canAccess}
        >
          {(namePrefix, index) => (
            <ProductAndServicesArrayRow
              name={namePrefix}
              index={index}
              firstLineLabel={t('product/ServiceName')}
              secondLineLabel={t('description')}
              disabled={!canAccess}
            />
          )}
        </FieldArrayComponent>
      </FormSectionCard>
      <FormSectionCard
        disabled={!canAccess}
        title={t('productsAndServicesForm.description.qualityStandards')}
        aiFieldName={concatWithDot(prefix, PRODUCTS_AND_SERVICES_DESCRIPTION_NAMES.QUALITY_STANDARDS)}
      >
        {/* 3.1.3 */}
        <TextInputControl
          multiline
          control={control}
          id="productsAndServicesForm.productAndServices.qualityStandards"
          name={concatWithDot(prefix, PRODUCTS_AND_SERVICES_DESCRIPTION_NAMES.QUALITY_STANDARDS)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
      <FormSectionCard
        disabled={!canAccess}
        title={t('productsAndServicesForm.description.achievedQualityCertifications')}
        collapsible
      >
        {/* 3.1.4 */}
        <FieldArrayComponent
          name={concatWithDot(prefix, PRODUCTS_AND_SERVICES_DESCRIPTION_NAMES.QUALITY_CERTIFICATIONS)}
          initialItem={productAndServicesArrayInitialValues}
          buttonText={t('certificate')}
          disabled={!canAccess}
        >
          {(namePrefix, index) => (
            <ProductAndServicesArrayRow
              disabled={!canAccess}
              name={namePrefix}
              index={index}
              firstLineLabel={t('certificate')}
              secondLineLabel={t('description')}
            />
          )}
        </FieldArrayComponent>
      </FormSectionCard>
      <FormSectionCard
        disabled={!canAccess}
        title={t('productsAndServicesForm.description.holdPatentsOrTrademarks')}
        collapsible
      >
        {/* 3.1.5 */}
        <FieldArrayComponent
          name={concatWithDot(prefix, PRODUCTS_AND_SERVICES_DESCRIPTION_NAMES.PATENTS_OR_TRADEMARKS)}
          initialItem={productAndServicesArrayInitialValues}
          buttonText={t('productsAndServicesForm.description.patentOrTrademark')}
          disabled={!canAccess}
        >
          {(namePrefix) => (
            <ProductAndServicesArrayRow
              name={namePrefix}
              firstLineLabel={t('productsAndServicesForm.description.patentOrTrademark')}
              secondLineLabel={t('description')}
              disabled={!canAccess}
            />
          )}
        </FieldArrayComponent>
      </FormSectionCard>
    </>
  );
};
